/*
 * VPDB - Virtual Pinball Database
 * Copyright (C) 2019 freezy <freezy@vpdb.io>
 *
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; either version 2
 * of the License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 * Modified for sigmashake-pinball by SigmaShake, 2026.
 */

// Port of the item updaters of vpx-js (lib/vpt/<kind>/<kind>-updater.ts).
// An updater applies one ItemFrame to the three.js objects of one item. The upstream updaters
// multiply matrices of the table frame. These update the rotation or the position of an
// object, in the three.js frame (see coords.ts).
//
// ItemFrame fields:
// - angle: radians, the same zero as the item angle in the table data.
// - offset: a signed shift in table units. A negative value moves the part down.
//   For a plunger it is the table y of the tip (a moving tip, not a shift).
// - visible: show or hide the item.

import type { BufferAttribute, BufferGeometry, Object3D } from "three";
import type { FlipperSpec, ItemFrame, PlungerSpec } from "../shared/contracts.ts";
import { plungerTipY, rodTipPositions } from "./meshes/plunger.ts";

/** Applies one frame to one item. */
export type ItemUpdater = (frame: ItemFrame) => void;

function finite(value: number | undefined): value is number {
  return value !== undefined && Number.isFinite(value);
}

/** Show or hide an object when the frame has a visible flag. */
export function applyVisible(obj: Object3D, frame: ItemFrame): void {
  if (frame.visible !== undefined) obj.visible = frame.visible;
}

/** A flipper turns about its pivot. The angle is absolute. */
export function flipperUpdater(group: Object3D, _spec: FlipperSpec): ItemUpdater {
  return (frame) => {
    applyVisible(group, frame);
    if (finite(frame.angle)) group.rotation.y = -frame.angle;
  };
}

/** A part moves up or down by the frame offset. `baseY` is its rest height in the three.js frame. */
export function offsetUpdater(
  group: Object3D,
  baseY: number,
  target: Object3D = group,
): ItemUpdater {
  return (frame) => {
    applyVisible(group, frame);
    if (finite(frame.offset)) target.position.y = baseY + frame.offset;
  };
}

/** How far a dropped target sits below its up position, in table units. The engine uses 52. */
export const DROP_TARGET_LIMIT = 52;

/**
 * A target moves down (a drop target: `offset`, 0 or less) or tilts back (a stand-up target:
 * `angle`, radians, 0 at rest). The tilt turns the group about its own x axis, so the group
 * must have the Euler order "YXZ". A positive angle tilts the top away from the player.
 */
export function targetUpdater(group: Object3D, baseY: number): ItemUpdater {
  return (frame) => {
    applyVisible(group, frame);
    if (finite(frame.offset)) group.position.y = baseY + frame.offset;
    if (finite(frame.angle)) group.rotation.x = -frame.angle;
  };
}

/** A gate leaf or a spinner plate turns about the x axis of the item. */
export function pivotUpdater(group: Object3D, pivot: Object3D, zeroAngle: number): ItemUpdater {
  return (frame) => {
    applyVisible(group, frame);
    if (finite(frame.angle)) pivot.rotation.x = -(frame.angle - zeroAngle);
  };
}

/** A plain item: only the visible flag changes. */
export function visibleUpdater(group: Object3D): ItemUpdater {
  return (frame) => applyVisible(group, frame);
}

/** Write the tip position of a plunger into the rod geometry. */
export function setPlungerTip(geometry: BufferGeometry, spec: PlungerSpec, tipY: number): void {
  const position = geometry.getAttribute("position") as BufferAttribute;
  const ys = rodTipPositions(spec, tipY);
  const count = Math.min(position.count, ys.length);
  for (let i = 0; i < count; i++) position.setZ(i, ys[i] as number);
  position.needsUpdate = true;
}

/** A plunger moves its tip. The offset is the table y of the tip. */
export function plungerUpdater(
  rod: Object3D,
  geometry: BufferGeometry,
  spec: PlungerSpec,
): ItemUpdater {
  return (frame) => {
    applyVisible(rod, frame);
    if (finite(frame.offset)) setPlungerTip(geometry, spec, plungerTipY(spec, frame.offset));
  };
}
